import { createApi, fetchBaseQuery } from "@reduxjs/toolkit/query/react";
import {
  IBaseData,
  IResponseBody,
} from "../../../../Interface/Api/BaseResponseInterface";
import {
  BannerImageSlider,
  FacilitySection,
  HomeGallerySectionType,
  IHomeRead,
  ImportantLinksCategory,
  ImportantLinksSection,
  INewsSectionRead,
  SearchByTextSchemaType,
  SliderImages,
  TabDataRead,
  WhatsNewCategory,
  WhatsNewRead,
  WhatsNewSection,
} from "../../../../Interface/Api/HomeInterface";

const BASE_URL = import.meta.env.VITE_APP_BASE_URL;

export const HomeSlice = createApi({
  reducerPath: "Home",
  baseQuery: fetchBaseQuery({
    baseUrl: BASE_URL,
  }),
  tagTypes: ["Home"],
  keepUnusedDataFor: 30000,
  endpoints: (builder) => ({
    getPublicSliderImages: builder.query({
      query: (lang) => ({
        url: `list-all-sliders/lang?lang=${lang}`,
        method: "GET",
      }),
      providesTags: ["Home"],
      transformResponse: (response: IResponseBody<IBaseData<SliderImages[]>>) =>
        response.data,
    }),
    getOfficeBearers: builder.query({
      query: (lang) => ({
        url: `list-all-manyavar/lang?lang=${lang}`,
        method: "GET",
      }),
      providesTags: ["Home"],
      transformResponse: (response: IResponseBody<IBaseData<IHomeRead[]>>) =>
        response.data,
    }),
    getNewsSection: builder.query({
      query: (lang) => ({
        url: `list-all-government-links/lang?lang=${lang}`,
        method: "GET",
      }),
      providesTags: ["Home"],
      transformResponse: (
        response: IResponseBody<IBaseData<INewsSectionRead[]>>
      ) => response.data,
    }),
    getTabData: builder.query({
      query: (lang) => ({
        url: `list-tabinfo/lang?lang=${lang}`,
        method: "GET",
      }),
      transformResponse: (response: IResponseBody<IBaseData<TabDataRead[]>>) =>
        response.data,
    }),
    getWhatsNew: builder.query<IBaseData<WhatsNewRead[]>, void>({
      query: () => ({
        url: "list-topfivesaptah",
        method: "GET",
      }),
      transformResponse: (response: IResponseBody<IBaseData<WhatsNewRead[]>>) =>
        response.data as IBaseData<WhatsNewRead[]>,
    }),
    getBannerImageSlider: builder.query<IBaseData<BannerImageSlider[]>, any>({
      query: (lang) => ({
        url: `banner-images/lang?lang=${lang}`,
        method: "GET",
      }),
      transformResponse: (
        response: IResponseBody<IBaseData<BannerImageSlider[]>>
      ) => response.data as IBaseData<BannerImageSlider[]>,
    }),
    getFacilitySection: builder.query<IBaseData<FacilitySection[]>, any>({
      query: (lang) => ({
        url: `list-facility/lang?lang=${lang}`,
        method: "GET",
      }),
      transformResponse: (
        response: IResponseBody<IBaseData<FacilitySection[]>>
      ) => response.data as IBaseData<FacilitySection[]>,
    }),
    getImportantLinksCategory: builder.query<
      IBaseData<ImportantLinksCategory[]>,
      any
    >({
      query: (lang) => ({
        url: `importantlink-master/lang?lang=${lang}`,
        method: "GET",
      }),
      transformResponse: (
        response: IResponseBody<IBaseData<ImportantLinksCategory[]>>
      ) => response.data as IBaseData<ImportantLinksCategory[]>,
    }),
    getImportantLinksSection: builder.query<
      IBaseData<ImportantLinksSection[]>,
      any
    >({
      query: (lang) => ({
        url: `list-all-importantlink/lang?lang=${lang}`,
        method: "GET",
      }),
      transformResponse: (
        response: IResponseBody<IBaseData<ImportantLinksSection[]>>
      ) => response.data as IBaseData<ImportantLinksSection[]>,
    }),
    getHomeGallerySection: builder.query<IBaseData<HomeGallerySectionType[]>, any>({
      query: (lang) => ({
        url: `list-photo-gallery/lang?lang=${lang}`,
        method: "GET",
      }),
      transformResponse: (
        response: IResponseBody<IBaseData<HomeGallerySectionType[]>>
      ) => response.data as IBaseData<HomeGallerySectionType[]>,
    }),
    getWhatsNewCategory: builder.query<IBaseData<WhatsNewCategory[]>, any>({
      query: (lang) => ({
        url: `whats-new-master/lang?lang=${lang}`,
        method: "GET",
      }),
      transformResponse: (
        response: IResponseBody<IBaseData<WhatsNewCategory[]>>
      ) => response.data as IBaseData<WhatsNewCategory[]>,
    }),
    getWhatsNewSection: builder.query<IBaseData<WhatsNewSection[]>, any>({
      query: ({ lang, payload }) => {
        const formData = new FormData();
        formData.append("id", payload);
        return {
          url: `whats-new/lang?lang=${lang}`,
          method: "POST",
          body: formData,
        };
      },
      transformResponse: (
        response: IResponseBody<IBaseData<WhatsNewSection[]>>
      ) => response.data as IBaseData<WhatsNewSection[]>,
    }),
    getWhatsNewMarquee: builder.query<IBaseData<WhatsNewSection[]>, any>({
      query: (lang) => {
        const formData = new FormData();
        formData.append("id", "9");
        return {
          url: `whats-new/lang?lang=${lang}`,
          method: "POST",
          body: formData,
        };
      },
      transformResponse: (
        response: IResponseBody<IBaseData<WhatsNewSection[]>>
      ) => response.data as IBaseData<WhatsNewSection[]>,
    }),
    getSearchByText: builder.query<IBaseData<SearchByTextSchemaType[]>, any>({
      query: (payload) => ({
        url: "api/search",
        method: "POST",
        body: {
          query: payload,
        },
      }),
      // invalidatesTags: ["search_slice_read"],
      transformResponse: (
        response: IResponseBody<IBaseData<SearchByTextSchemaType[]>>
      ) => response.data as IBaseData<SearchByTextSchemaType[]>,
    }),
  }),
});

export const {
  useGetPublicSliderImagesQuery,
  useGetOfficeBearersQuery,
  useGetTabDataQuery,
  useGetNewsSectionQuery,
  useGetWhatsNewQuery,
  useGetBannerImageSliderQuery,
  useGetFacilitySectionQuery,
  useGetHomeGallerySectionQuery,
  useLazyGetSearchByTextQuery,
  useGetWhatsNewCategoryQuery,
  useLazyGetWhatsNewSectionQuery,
  useLazyGetWhatsNewMarqueeQuery,
  useGetImportantLinksCategoryQuery,
  useGetImportantLinksSectionQuery,
} = HomeSlice;
